import { fileExtension } from '../../utils/format';

/**
 * MIME types whose names a user would never guess from the string itself.
 * Anything not listed falls through to the family/extension rules below.
 */
const EXACT_TYPES = {
  'application/pdf': 'PDF document',
  'application/msword': 'Word document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
  'application/vnd.ms-excel': 'Excel spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel spreadsheet',
  'application/vnd.ms-powerpoint': 'PowerPoint presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PowerPoint presentation',
  'application/zip': 'ZIP file',
  'application/json': 'JSON file',
};

/** Subtypes whose canonical short name differs from the mime subtype. */
const SUBTYPE_ALIASES = {
  mpeg: 'MP3',
  quicktime: 'MOV',
  'svg+xml': 'SVG',
  jpeg: 'JPEG',
  plain: 'TXT',
};

/** `image` -> "PNG image", `text` -> "TXT text file". */
const FAMILY_SUFFIX = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  text: 'text file',
};

/**
 * Human label for a file's type — "PDF document" rather than
 * "application/pdf". Prefers the mime type, falls back to the extension when
 * the server only knows `application/octet-stream`.
 */
export function fileTypeLabel(mimeType, name) {
  const mime = (mimeType ?? '').toLowerCase();
  if (EXACT_TYPES[mime]) return EXACT_TYPES[mime];

  const [family, subtype = ''] = mime.split('/');
  const suffix = FAMILY_SUFFIX[family];
  if (suffix && subtype) {
    const short = SUBTYPE_ALIASES[subtype] ?? subtype;
    return `${short.toUpperCase()} ${suffix}`;
  }

  const ext = fileExtension(name ?? '');
  if (ext) return `${ext.toUpperCase()} file`;
  return 'Unknown';
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

/**
 * Coarse "how long ago" label to sit beside an absolute timestamp. `now` is
 * injectable so the output is deterministic under test.
 */
export function relativeTime(iso, now = new Date()) {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const elapsed = now.getTime() - then.getTime();
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) return plural(Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return plural(Math.floor(elapsed / HOUR), 'hour');
  if (elapsed < 2 * DAY) return 'yesterday';
  if (elapsed < MONTH) return plural(Math.floor(elapsed / DAY), 'day');
  if (elapsed < YEAR) return plural(Math.floor(elapsed / MONTH), 'month');
  return plural(Math.floor(elapsed / YEAR), 'year');
}

/** Breadcrumb chain as a single readable path; the workspace root reads "Home". */
export function folderPathLabel(crumbs) {
  if (!crumbs?.length) return 'Home';
  return crumbs.map((c) => c.name).join(' / ');
}
